<template>
  <div>
    <v-autocomplete
      v-bind="$attrs"
      :value="value"
      :items="itemsWithCreate"
      :search-input.sync="search"
      :filter="filter"
      @change="onChange"
    >
      <template v-for="(_, slot) in $scopedSlots" v-slot:[slot]="scope">
        <slot :name="slot" v-bind="scope" />
      </template>
      <template v-slot:item="{ item }">
        <v-list-item-title v-if="item.value === CREATE_COLOR_SENTINEL" class="blue--text font-weight-medium">
          <v-icon small color="blue" class="mr-1">mdi-plus-circle-outline</v-icon>Create
        </v-list-item-title>
        <v-list-item-title v-else>{{ item.text }}</v-list-item-title>
      </template>
    </v-autocomplete>

    <AppDialog
      :show.sync="dialog"
      max-width="500"
      persistent
      icon="mdi-palette"
      title="Add Color"
      subtitle="Adds it to Master > Colors and selects it here"
      @close="closeDialog"
    >
      <v-form ref="form" v-model="valid" @submit.prevent="save">
        <v-text-field
          v-model="form.name"
          label="Color Name *"
          placeholder="e.g. Bottle Green"
          :rules="[(v) => !!(v && v.trim()) || 'This field is required']"
          :error-messages="createError"
          hint="Saved in capitals, e.g. BOTTLE GREEN"
          persistent-hint
          outlined
          autofocus
        ></v-text-field>
      </v-form>

      <template v-slot:actions>
        <v-btn text :disabled="isCreating" @click="closeDialog">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="isCreating"
          :disabled="!valid || isCreating"
          @click="save"
        >
          Create
        </v-btn>
      </template>
    </AppDialog>
  </div>
</template>

<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";

const CREATE_COLOR_SENTINEL = "__create_color__";

// Color dropdown backed by Master > Colors. When the typed text doesn't match
// an existing color it offers a "Create" row that opens a small dialog
// (pre-filled with what was typed) to add the color to the master list, then
// selects it - without leaving the current form.
// `items` are { id, text, value } rows; the parent owns that list and should
// push the row emitted with "created" so every other picker sees it too.
export default {
  name: "ColorAutocomplete",
  components: { AppDialog },
  inheritAttrs: false,
  props: {
    value: { type: String, default: null },
    items: { type: Array, default: () => [] },
  },
  data() {
    return {
      CREATE_COLOR_SENTINEL,
      search: "",
      dialog: false,
      valid: false,
      form: { name: "" },
      isCreating: false,
      createError: "",
    };
  },
  computed: {
    typedName() {
      return (this.search || "").trim().toUpperCase();
    },
    itemsWithCreate() {
      if (!this.typedName) {
        return this.items;
      }
      const exists = this.items.some(
        (item) =>
          (item.text || "").trim().toUpperCase() === this.typedName ||
          (item.value || "").trim().toUpperCase() === this.typedName
      );
      return exists
        ? this.items
        : [...this.items, { text: "Create", value: CREATE_COLOR_SENTINEL }];
    },
  },
  methods: {
    // Keep the "Create" row visible whatever was typed - its own label would
    // otherwise be filtered out by Vuetify's substring match.
    filter(item, queryText, itemText) {
      if (item.value === CREATE_COLOR_SENTINEL) {
        return true;
      }
      return (itemText || "").toLowerCase().indexOf((queryText || "").toLowerCase()) > -1;
    },
    onChange(value) {
      if (value === CREATE_COLOR_SENTINEL) {
        this.openDialog(this.typedName);
        return;
      }
      this.$emit("input", value);
    },
    openDialog(name) {
      // Put the field back to what it was - the sentinel isn't a real color.
      const previous = this.value;
      this.$emit("input", null);
      this.$nextTick(() => this.$emit("input", previous));
      this.form = { name };
      this.createError = "";
      this.dialog = true;
    },
    closeDialog() {
      if (!this.isCreating) {
        this.dialog = false;
      }
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.isCreating = true;
      this.createError = "";
      try {
        const response = await axios.post("/colors", { name: this.form.name });
        const color = response.data.data;
        this.$emit("created", { id: color.id, text: color.description, value: color.code });
        this.$emit("input", color.code);
        this.search = "";
        this.dialog = false;
      } catch (e) {
        console.log(e);
        const errors = e.response && e.response.data && e.response.data.errors;
        this.createError = (errors && errors.name && errors.name[0]) || "Could not create this color.";
      } finally {
        this.isCreating = false;
      }
    },
  },
};
</script>
